import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import * as qs from 'qs';
import { Navigate } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_PLAY_EVENTTYPE,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { isFixtureLive, isFixtureOpened } from '@sorare/gameplay/src/lib/so5';

import type {
  LineupRedirectQuery,
  LineupRedirectQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const LINEUP_REDIRECT_QUERY = gql`
  query LineupRedirectQuery($id: ID!) {
    so5 {
      so5Lineup(id: $id) {
        id
        so5Leaderboard {
          slug
        }
        so5LeaderboardContender {
          slug
        }
        so5Fixture {
          slug
          type
          ...isFixtureLive_so5Fixture
          ...isFixtureOpened_so5Fixture
        }
      }
    }
  }
  ${isFixtureLive.fragments.so5Fixture}
  ${isFixtureOpened.fragments.so5Fixture}
` as TypedDocumentNode<LineupRedirectQuery, LineupRedirectQueryVariables>;

const LineupRedirect = () => {
  const { generateSportContextPath } = useSportContext();
  const { id } = useTypedParams<PageParams>();
  const { data } = useQuery(LINEUP_REDIRECT_QUERY, {
    variables: {
      id: id!,
    },
  });

  if (!data) return null;

  const lineup = data.so5.so5Lineup;

  if (!lineup.so5Leaderboard?.slug) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE, {
          params: {
            eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
          },
        })}
      />
    );
  }

  if (isFixtureOpened(lineup.so5Fixture) || isFixtureLive(lineup.so5Fixture)) {
    return (
      <Navigate
        replace
        to={`${generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
          params: {
            fixture: lineup.so5Fixture.slug,
            eventType: lineup.so5Fixture.type.toLowerCase(),
          },
        })}${qs.stringify(
          {
            id: idFromObject(id),
          },
          {
            addQueryPrefix: true,
          }
        )}`}
      />
    );
  }

  return (
    <Navigate
      replace
      to={`${generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
        {
          params: {
            fixture: lineup.so5Fixture.slug,
            eventType: lineup.so5Fixture.type.toLowerCase(),
            contender: lineup.so5LeaderboardContender.slug,
          },
        }
      )}${qs.stringify(
        {
          id: idFromObject(id),
        },
        {
          addQueryPrefix: true,
        }
      )}`}
    />
  );
};

export default LineupRedirect;
